import { forwardRef, useState } from 'react';
import { Card, CardTitle, IconButton, Input } from 'components/UI';
import { Badge, Box, HStack, SystemStyleObject } from '@chakra-ui/react';
import { FaCheck, FaEdit, FaTimes, FaTrashAlt } from 'react-icons/fa';

export interface GroupContainerProps {
  children: React.ReactNode;
  columns?: number;
  label?: string;
  horizontal?: boolean;
  hover?: boolean;
  handleProps?: React.HTMLAttributes<any>;
  scrollable?: boolean;
  shadow?: boolean;
  placeholder?: boolean;
  unstyled?: boolean;
  onClick?(): void;
  onRemove?(): void;
  categoryEdit(arg0: string, arg1: string): void;
  primary?: boolean;
  sx?: SystemStyleObject;
}

const GroupContainer = forwardRef<HTMLDivElement, GroupContainerProps>(
  (
    {
      children,
      handleProps,
      horizontal,
      hover,
      onClick,
      onRemove,
      label,
      placeholder,
      scrollable,
      shadow,
      unstyled,
      categoryEdit,
      primary,
      sx,
      ...props
    }: GroupContainerProps,
    ref
  ) => {
    const [isEditing, setIsEditing] = useState(false);
    const [value, setValue] = useState(label);
    const [inputValue, setInputValue] = useState(label);

    const Component = onClick ? 'button' : 'div';

    const renderEditIcon = !isEditing ? (
      <IconButton
        aria-label="Edit"
        onClick={() => setIsEditing(true)}
        variant="unstyled"
        as={FaEdit}
        h={4}
        _hover={{ h: 5 }}
      />
    ) : null;

    const renderBadge = primary ? (
      <Badge
        sx={{
          borderRadius: 'full',
          px: 2,
          fontSize: 'sm',
          fontWeight: 'bold',
        }}
      >
        Primary
      </Badge>
    ) : null;

    const handleCancel = () => {
      setIsEditing(false);
      setInputValue(value);
    };

    const handleSave = () => {
      setIsEditing(false);
      setValue(inputValue);
      if (!label || !inputValue) return;
      categoryEdit(label, inputValue);
    };

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(e.target.value);
    };

    const renderInput = isEditing ? (
      <HStack>
        <Input
          sx={{
            color: 'black',
            minWidth: 100,
            h: 8,
          }}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={(e) => {
            if (e.key !== 'Enter') return;
            handleSave();
          }}
        />
        <IconButton
          aria-label="Cancel"
          onClick={handleCancel}
          variant="unstyled"
          as={FaTimes}
          h={4}
          _hover={{ h: 5 }}
        />
        <IconButton aria-label="Save" onClick={handleSave} variant="unstyled" as={FaCheck} h={4} _hover={{ h: 5 }} />
      </HStack>
    ) : (
      <Box>{value}</Box>
    );

    const renderRemoveIcon = onRemove ? (
      <IconButton aria-label="Remove" onClick={onRemove} variant="unstyled" as={FaTrashAlt} h={4} _hover={{ h: 5 }} />
    ) : null;

    const renderCardBody = label ? (
      <Card mb={2.5}>
        <CardTitle
          bgColor="primaryThemeColour.400"
          color="white"
          onClick={onClick}
          tabIndex={onClick ? 0 : undefined}
          h={12}
          ref={ref}
          {...handleProps}
          cursor="grab"
        >
          <HStack>
            {renderInput}
            {renderEditIcon}
            {renderBadge}
          </HStack>

          {renderRemoveIcon}
        </CardTitle>
        <Box>{children}</Box>
      </Card>
    ) : null;

    return (
      <Box sx={sx}>
        <Component {...props} ref={ref as React.RefObject<any>}>
          {renderCardBody}
        </Component>
      </Box>
    );
  }
);

export default GroupContainer;
